import { createTheme } from '@mui/material/styles';

const borderColor = '#e2e8f0';

const theme = createTheme({
  palette: {
    primary: {
      main: '#185aa4',
      light: '#e7f0fa',
      dark: '#10477f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0f766e',
      light: '#dff5f2',
      dark: '#115e59',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f6f8fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#172033',
      secondary: '#64748b',
    },
    divider: borderColor,
  },
  typography: {
    fontFamily: 'Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h1: { fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 700, lineHeight: 1.15 },
    h2: { fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', fontWeight: 700, lineHeight: 1.2 },
    h3: { fontSize: 'clamp(1.5rem, 2.5vw, 1.875rem)', fontWeight: 700, lineHeight: 1.25 },
    h4: { fontSize: 'clamp(1.35rem, 2vw, 1.65rem)', fontWeight: 700, lineHeight: 1.3 },
    h5: { fontSize: '1.2rem', fontWeight: 650, lineHeight: 1.35 },
    h6: { fontSize: '1rem', fontWeight: 650, lineHeight: 1.4 },
    body1: { fontSize: '0.975rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    button: { fontWeight: 650 },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(15, 23, 42, 0.05)',
    '0 4px 12px rgba(15, 23, 42, 0.06)',
    '0 8px 24px rgba(15, 23, 42, 0.08)',
    ...Array(21).fill('0 12px 32px rgba(15, 23, 42, 0.10)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { backgroundColor: '#f6f8fb' },
        body: { minWidth: 320 },
        '::selection': { backgroundColor: '#cfe1f5' },
        'a': { color: '#185aa4' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 10,
          padding: '8px 16px',
          textTransform: 'none',
        },
        contained: {
          '&:hover': { boxShadow: '0 4px 12px rgba(24, 90, 164, 0.20)' },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${borderColor}`,
          borderRadius: 14,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 14 },
        elevation1: { boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)' },
        elevation2: { boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)' },
        elevation3: { boxShadow: '0 8px 24px rgba(15, 23, 42, 0.07)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#185aa4',
          boxShadow: '0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 16px rgba(15, 23, 42, 0.12)',
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#ffffff',
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#64748b' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#185aa4',
            borderWidth: 2,
          },
        },
        notchedOutline: { borderColor: '#94a3b8' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: '#64748b',
          '&.Mui-checked': {
            color: '#ffffff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#185aa4',
              borderColor: '#185aa4',
              opacity: 1,
            },
          },
        },
        thumb: {
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.35)',
        },
        track: {
          backgroundColor: '#d7e0ea',
          border: '1px solid #94a3b8',
          opacity: 1,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { border: `1px solid ${borderColor}`, borderRadius: 12 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor, padding: '14px 16px' },
        head: {
          backgroundColor: '#f8fafc',
          color: '#475569',
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&.MuiTableRow-hover:hover': { backgroundColor: '#f8fafc' } },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 8, fontWeight: 600 } },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 10, '&:hover': { backgroundColor: '#eef4fa' } },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
  },
});

export default theme;
